import { createContext, useContext, ReactNode, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import type { users } from '@prisma/client';
import { RootState, useAppDispatch } from '../store/store';
import { logout, validate } from '../store/slices/auth';
import { validate as validateApi } from '../api/services/authentication';

interface AuthContextType {
  isAuthenticated: boolean;
  logout: () => void;
  currentUser: users | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const dispatch = useAppDispatch();
  const isAuthenticated = !!currentUser;
  const navigate = useNavigate();

  const logoutDispatch = () => {
    localStorage.removeItem('jwtToken');
    dispatch(logout());
    navigate('/login');
  };

  // Re-check the token when the tab regains focus so an expired session logs out cleanly.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && currentUser) {
        validateApi().catch(() => logoutDispatch());
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) {
      dispatch(validate());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, logout: logoutDispatch, currentUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
